import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import {
  EyeIconClose,
  EyeIconOpen,
  getMaxLength,
  ModalCustomStyle,
  HelpIcon,
} from 'common';
import {
  Button,
  Label,
  ErrorMessage,
  InputField,
  CloseIconBlock,
} from '@sentara-health/optimahealth_web';
import PasswordInfoOverlay from '../pages/forms/PasswordInfoOverlay';
import Modal from 'react-modal';

// Define the interface for the props passed to ConfirmPasswordInput component
export interface PasswordChangeProps {
  // Handler for onKeyDown event
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  showPasswordPlaceholder?: string;
  showIcon?: boolean;
  className?: string;
}

// Define the ConfirmPasswordInput component
const ConfirmPassword: React.FC<PasswordChangeProps> = ({
  onKeyDown,
  showPasswordPlaceholder,
  showIcon,
  className,
}) => {
  const { t } = useTranslation();

  // State to manage password visibility
  const [confirmPasswordOpen, setConfirmPasswordOpen] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const methods = useFormContext();
  const {
    getValues,
    formState: { errors },
  } = methods;

  function openModal(e: { preventDefault: () => void }) {
    e.preventDefault();
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  return (
    <>
      <div className="col-12">
        <div className="row">
          {/* Label for the input */}
          <div className="col-12">
            <Label
              dataTestId={'confirmPasswordInputLabel'}
              htmlFor="password"
              className="form-label"
            >
              {t('common.confirmPassword')}
            </Label>
            {/* Display asterisk if password length is less than 2 */}
            <span className="text-danger px-1">*</span>

            {/* Container for input and icon */}
            <div className="d-flex align-items-center">
              <div className="position-relative flex-grow-1">
                {/* Input field */}
                <InputField
                  className={
                    errors?.confirmPassword ? 'error-border pe-5' : 'pe-5'
                  }
                  autoComplete="new-password"
                  data-openreplay-masked={true}
                  maxLength={getMaxLength.password}
                  type={!confirmPasswordOpen ? 'password' : 'text'}
                  dataTestId="confirmPasswordInput"
                  placeholder={showPasswordPlaceholder ?? '********'}
                  onKeyDown={onKeyDown}
                  {...methods.register('confirmPassword', {
                    required: true,
                    validate: (value) => {
                      return (
                        value === getValues('password') ||
                        'Password does not match'
                      );
                    },
                  })}
                />

                {/* Button to toggle confirm password visibility */}
                <Button
                  id="eyeIconButtonPasswordChange"
                  dataTestId="confirmPasswordInputEyeIcon"
                  variant="icon_btn"
                  className="position-absolute top-50 end-0 translate-middle-y"
                  onClick={(e: { preventDefault: () => void }) => {
                    e.preventDefault();
                    setConfirmPasswordOpen(!confirmPasswordOpen);
                  }}
                >
                  {!confirmPasswordOpen ? <EyeIconClose /> : <EyeIconOpen />}
                </Button>
              </div>

              {/* Button to open confirm password information modal */}
              <Button
                data-testid="memIdHelpInfo"
                onClick={openModal}
                aria-label="Information icon click to view"
                variant="icon_btn"
                type={'button'}
                className="ms-2 cursor-pointer"
                iconLeft={<HelpIcon />}
              />
            </div>

            {/* Display error messages */}
            {errors?.confirmPassword?.type === 'required' && (
              <div className="col-11">
                <ErrorMessage dataTestId={'confirmPasswordRequiredError'}>
                  {window.location.pathname.includes('activation')
                    ? t('profile.confirmPwd')
                    : t('common.confirmPwd')}
                </ErrorMessage>
              </div>
            )}

            {errors?.confirmPassword?.type === 'validate' && (
              <div className="col-11">
                <ErrorMessage dataTestId={'confirmPasswordRequiredError'}>
                  {t('common.confirmPasswordrequired')}
                </ErrorMessage>
              </div>
            )}
          </div>
        </div>

        {/* Modal for password information */}
        <Modal
          isOpen={isOpen}
          onRequestClose={closeModal}
          data-testid="userNameInputModal"
          contentLabel="Username Information Overlay"
          style={ModalCustomStyle}
        >
          <div
            className="w-member-overlay"
            data-testid="userNameInputModalModal"
          >
            <div className="d-flex justify-content-end mb-2">
              <Button
                type="button"
                data-testid="userNameInputModalClose"
                variant="icon_btn"
                onClick={closeModal}
                aria-label="Close"
              >
                <CloseIconBlock />
              </Button>
            </div>
            <PasswordInfoOverlay />
          </div>
        </Modal>
      </div>
    </>
  );
};

export default ConfirmPassword;